import { Brand } from "@/data/brands";

/** Zone definition */
export interface HorizonZone {
  key: string;
  min: number;
  max: number;
  color: string;
  labelFr: string;
  labelEn: string;
  /** Visual bottom (0-1 fraction of container height) */
  visualBottom: number;
  /** Visual top */
  visualTop: number;
}

/**
 * Four zones with EQUAL visual height (each 25% of the container).
 * Ordered bottom-to-top.
 */
export const ZONES: HorizonZone[] = [
  { key: "alert",        min: 0,  max: 49,  color: "#A83232", labelFr: "Alerte",       labelEn: "Alert",        visualBottom: 0,    visualTop: 0.25 },
  { key: "insufficient", min: 50, max: 64,  color: "#E8943D", labelFr: "Insuffisant",  labelEn: "Insufficient", visualBottom: 0.25, visualTop: 0.50 },
  { key: "serious",      min: 65, max: 84,  color: "#4E7E6A", labelFr: "Sérieux",      labelEn: "Serious",      visualBottom: 0.50, visualTop: 0.75 },
  { key: "exemplary",    min: 85, max: 100, color: "#1B5E20", labelFr: "Exemplaire",   labelEn: "Exemplary",    visualBottom: 0.75, visualTop: 1.0  },
];

/** Find which zone a score belongs to */
export function getZoneForScore(score: number): HorizonZone {
  return ZONES.find(z => score >= z.min && score <= z.max) ?? ZONES[0];
}

/**
 * Map a raw score (0-100) to a visual Y position (0-1).
 * Each zone occupies exactly 25% of the visual height.
 * Within each zone, Y is proportional to the score.
 * Applies inner padding (in pixels) to prevent logos from touching zone borders.
 */
export function scoreToVisualY(score: number, containerHeight: number, paddingPx: number = 25): number {
  const zone = getZoneForScore(score);
  const scoreRange = zone.max - zone.min || 1;
  const fraction = (score - zone.min) / scoreRange;

  const zoneHeightPx = (zone.visualTop - zone.visualBottom) * containerHeight;
  const paddingFrac = paddingPx / containerHeight;

  // Clamp within zone with padding
  const innerBottom = zone.visualBottom + paddingFrac;
  const innerTop = zone.visualTop - paddingFrac;
  const innerHeight = Math.max(innerTop - innerBottom, 0.001);

  return innerBottom + fraction * innerHeight;
}

export interface BeeswarmPosition {
  x: number; // 0-1 fraction
  visualY: number; // 0-1 fraction
}

/**
 * Named priority brands per zone. These get logo display by default.
 * Names are lowercased for matching.
 */
export const FEATURED_BRAND_NAMES: Record<string, string[]> = {
  exemplary: [
    "colourful standard", "balzac paris", "hopaal", "bonnegueule",
    "loom", "1083", "salomon", "nudie jeans", "hast", "mister k",
  ],
  serious: [
    "faguo", "mud jeans", "andré", "petit bateau", "armedangels",
    "gucci", "hugo boss", "veja", "patagonia", "the north face",
  ],
  insufficient: [
    "prada", "etam", "timberland", "asics", "lacoste",
    "levi's", "guess", "jott", "nike", "puma",
  ],
  alert: [
    "shein", "temu", "primark", "forever 21", "zara",
    "adidas", "tommy hilfiger", "new balance", "h&m", "converse",
  ],
};

/**
 * Determine which brands get logos based on the named priority list.
 * Returns a Set of brand IDs that should display as logos.
 */
export function selectFeaturedBrands(brands: Brand[]): Set<string> {
  const featured = new Set<string>();
  const brandsByZone: Record<string, Brand[]> = {};

  ZONES.forEach(z => (brandsByZone[z.key] = []));
  brands.forEach(b => {
    const zone = getZoneForScore(b.overallScore);
    brandsByZone[zone.key].push(b);
  });

  ZONES.forEach(zone => {
    const priorityNames = FEATURED_BRAND_NAMES[zone.key] || [];
    const zoneBrands = brandsByZone[zone.key];

    // Match by name (case-insensitive)
    for (const name of priorityNames) {
      const match = zoneBrands.find(b => b.name.toLowerCase() === name);
      if (match) featured.add(match.id);
    }

    // If we didn't fill 10, add top scorers from this zone
    const currentCount = zoneBrands.filter(b => featured.has(b.id)).length;
    if (currentCount < 10) {
      const sorted = [...zoneBrands]
        .filter(b => !featured.has(b.id))
        .sort((a, b) => b.overallScore - a.overallScore);
      for (let i = 0; i < Math.min(10 - currentCount, sorted.length); i++) {
        featured.add(sorted[i].id);
      }
    }
  });

  return featured;
}

/**
 * Force-directed simulation on X-axis only.
 * Places brands using full horizontal width with iterative repulsion.
 * Logos are placed first with stronger repulsion radius.
 */
export function computeBeeswarm(
  brands: Brand[],
  containerHeight: number,
  featuredIds: Set<string>
): Map<string, BeeswarmPosition> {
  const ZONE_PADDING_PX = 25;
  // Detect mobile via container height heuristic (or use smaller radii)
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const LOGO_RADIUS_PX = isMobile ? 14 : 28;
  const DOT_RADIUS_PX = isMobile ? 3 : 8;
  const H_MIN = 0.04;
  const H_MAX = 0.95;
  const H_CENTER = (H_MIN + H_MAX) / 2;

  interface PlacedItem {
    id: string;
    x: number;       // 0-1
    yPx: number;     // pixels from bottom
    radiusPx: number; // collision radius in pixels
  }

  const items: PlacedItem[] = [];
  const positions = new Map<string, BeeswarmPosition>();

  // Compute all visual Y positions first
  const brandData = brands.map(b => ({
    brand: b,
    visualY: scoreToVisualY(b.overallScore, containerHeight, ZONE_PADDING_PX),
    yPx: scoreToVisualY(b.overallScore, containerHeight, ZONE_PADDING_PX) * containerHeight,
    isLogo: featuredIds.has(b.id),
    radius: featuredIds.has(b.id) ? LOGO_RADIUS_PX : DOT_RADIUS_PX,
  }));

  // Sort: logos first (by score desc), then dots (by score desc)
  brandData.sort((a, b) => {
    if (a.isLogo !== b.isLogo) return a.isLogo ? -1 : 1;
    return b.brand.overallScore - a.brand.overallScore;
  });

  // Container width approximation for collision detection (px)
  const containerWidthPx = containerHeight * 1.2; // approximate aspect ratio

  for (const item of brandData) {
    const { brand, visualY, yPx, radius } = item;

    // Find best X position using symmetric expansion from center
    let bestX = H_CENTER;
    let bestClearance = -Infinity;

    // Scan symmetric offsets from center
    const step = 0.012;
    const maxOffset = (H_MAX - H_MIN) / 2;

    for (let offset = 0; offset <= maxOffset; offset += step) {
      const candidates = offset === 0
        ? [H_CENTER]
        : [H_CENTER - offset, H_CENTER + offset];

      for (const cx of candidates) {
        if (cx < H_MIN || cx > H_MAX) continue;

        let minClearance = Infinity;
        for (const placed of items) {
          const dxPx = (cx - placed.x) * containerWidthPx;
          const dyPx = yPx - placed.yPx;
          const dist = Math.sqrt(dxPx * dxPx + dyPx * dyPx);
          const required = (radius + placed.radiusPx) * 1.15;
          minClearance = Math.min(minClearance, dist - required);
        }

        if (minClearance > bestClearance) {
          bestClearance = minClearance;
          bestX = cx;
          // If we have ample clearance, stop searching
          if (minClearance > radius * 2) {
            break;
          }
        }
      }

      // Early exit if we found a clear spot
      if (bestClearance > (radius * 2)) break;
    }

    items.push({ id: brand.id, x: bestX, yPx, radiusPx: radius });
    positions.set(brand.id, { x: bestX, visualY });
  }

  // Run a few iterations of force-directed repulsion on X only
  const ITERATIONS = 8;
  const REPULSION_STRENGTH = 0.3;

  for (let iter = 0; iter < ITERATIONS; iter++) {
    for (let i = 0; i < items.length; i++) {
      let forceX = 0;
      const a = items[i];

      for (let j = 0; j < items.length; j++) {
        if (i === j) continue;
        const b = items[j];

        const dxPx = (a.x - b.x) * containerWidthPx;
        const dyPx = a.yPx - b.yPx;
        const dist = Math.sqrt(dxPx * dxPx + dyPx * dyPx);
        const minDist = (a.radiusPx + b.radiusPx) * 1.3;

        if (dist < minDist && dist > 0.01) {
          // Only push on X axis
          const pushX = (dxPx / dist) * (minDist - dist) * REPULSION_STRENGTH;
          forceX += pushX / containerWidthPx; // convert back to fraction
        }
      }

      // Apply force with clamping
      a.x = Math.max(H_MIN, Math.min(H_MAX, a.x + forceX));
    }
  }

  // Update final positions
  for (const item of items) {
    const existing = positions.get(item.id);
    if (existing) {
      positions.set(item.id, { x: item.x, visualY: existing.visualY });
    }
  }

  return positions;
}
